
import { Component, Prop } from 'vue-property-decorator';
import ListBase from '@/lib/listbase';
import VWidget from '@/components/VWidget.vue';
import VueApexCharts from 'vue-apexcharts';

interface MonthlySummaryEntry {
  star_date: Record<string, number>;
  end_date: Record<string, number>;
}

interface YearCard {
  year: number;
  total: number;
}

@Component({
  components: { apexcharts: VueApexCharts, VWidget }
})
export default class SelectedDashboard extends ListBase {
  @Prop({ required: true }) rangeFrom!: any;
  @Prop({ required: true }) rangeTo!: any;
  @Prop({ required: true }) monthlySummary!: Record<string, MonthlySummaryEntry>;
  @Prop({ required: true, type: String }) dashboardType!: string;
  @Prop({ required: true, type: Array }) backendDealers!: string[];
  @Prop({ required: true, type: Array }) selectedDealers!: number[];

  options: any = {
    chart: {
      type: 'bar',
      height: 350,
      stacked: false,
      zoom: {
        enabled: true,
        type: 'x',
        autoScaleYaxis: true
      }
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '55%',
        endingShape: 'rounded'
      }
    },
    dataLabels: {
      enabled: true,
      formatter: (value: number) => new Intl.NumberFormat().format(value)
    },
    title: {
      text: this.dashboardType,
      align: 'left'
    },
    xaxis: {
      type: 'text',
      categories: []
    },
    yaxis: {
      labels: {
        formatter: (val: number) => val.toLocaleString()
      }
    },
    legend: {
      position: 'top',
      horizontalAlign: 'center',
      floating: true,
      offsetY: -15
    }
  };

  handleDealerChange(ids: number[]) {
    this.$emit('update:selected-dealers', ids);
  }

  get series() {
    if (!this.monthlySummary) return [];
    const monthKeys = Object.keys(this.monthlySummary).sort((a, b) => parseInt(a) - parseInt(b));

    if (this.rangeFrom.year === this.rangeTo.year) {
      const combinedData = monthKeys.map(m => {
        const starObj = this.monthlySummary[m].star_date || {};
        const endObj = this.monthlySummary[m].end_date || {};
        let total = 0;
        this.selectedDealersNames.forEach(dealerName => {
          total += Number(starObj[dealerName] || 0) + Number(endObj[dealerName] || 0);
        });
        return total;
      });
      return [{ name: 'Total', data: combinedData }];
    } else {
      const starData = monthKeys.map(m => {
        const starObj = this.monthlySummary[m].star_date || {};
        let totalStar = 0;
        this.selectedDealersNames.forEach(dealerName => {
          totalStar += Number(starObj[dealerName] || 0);
        });
        return totalStar;
      });

      const endData = monthKeys.map(m => {
        const endObj = this.monthlySummary[m].end_date || {};
        let totalEnd = 0;
        this.selectedDealersNames.forEach(dealerName => {
          totalEnd += Number(endObj[dealerName] || 0);
        });
        return totalEnd;
      });

      return [
        { name: String(this.rangeFrom.year), data: starData },
        { name: String(this.rangeTo.year), data: endData }
      ];
    }
  }

  getInitials(str: string) {
    return str
      .split(' ')
      .map(n => n[0])
      .join('')
      .substring(0, 2);
  }

  get listDealers() {
    const dealers = this.backendDealers.map((dealerName: string, index: number) => ({
      id: index + 1,
      name: dealerName
    }));
    return [{ id: 0, name: 'Todos' }, ...dealers];
  }

  get allDealersSelected() {
    const allDealerIds = this.listDealers.filter(d => d.id !== 0).map(d => d.id);
    return this.selectedDealers.length === allDealerIds.length && this.selectedDealers.length > 0;
  }

  get selectedDealersNames(): string[] {
    const ids = this.selectedDealers.filter(id => id !== 0);
    return ids.map(selectedId => {
      const found = this.listDealers.find(d => d.id === selectedId);
      return found ? found.name : '';
    });
  }

  get chartOptions() {
    if (!this.monthlySummary) {
      return { ...this.options, xaxis: { categories: [] } };
    }
    const monthKeys = Object.keys(this.monthlySummary).sort((a, b) => parseInt(a) - parseInt(b));
    const categories = monthKeys.map(m =>
      this.gCommonList('months')[parseInt(m) - 1].name.substr(0, 3)
    );
    return {
      ...this.options,
      xaxis: {
        categories
      }
    };
  }

  get yearCards() {
    if (!this.monthlySummary) return [];
    const [startYearTotal, endYearTotal] = this.calculateYearTotals();

    return this.buildYearCards(startYearTotal, endYearTotal);
  }

  calculateYearTotals(): [number, number] {
    return Object.values(this.monthlySummary).reduce<[number, number]>(
      ([totalStar, totalEnd], monthData) => {
        const starTotal = this.sumDealerValues(monthData.star_date);
        const endTotal = this.sumDealerValues(monthData.end_date);
        return [totalStar + starTotal, totalEnd + endTotal];
      },
      [0, 0]
    );
  }

  sumDealerValues(dateData: Record<string, number> = {}): number {
    return this.selectedDealersNames.reduce(
      (sum, dealer) => sum + (Number(dateData[dealer]) || 0),
      0
    );
  }

  buildYearCards(startTotal: number, endTotal: number): YearCard[] {
    const cards: YearCard[] = [];
    const shouldCombineYears = this.rangeFrom.year === this.rangeTo.year;

    cards.push({ year: this.rangeFrom.year, total: startTotal });

    if (!shouldCombineYears) {
      cards.push({ year: this.rangeTo.year, total: endTotal });
    }

    return cards;
  }

  get isChartLoading(): boolean {
    return !this.monthlySummary;
  }

  L(text: string) {
    return text;
  }

  removeDealer(dealer: any) {
    let newSelected = [...this.selectedDealers];
    if (dealer.id === 0) {
      newSelected = [];
    } else {
      newSelected = newSelected.filter(id => id !== dealer.id);
    }
    this.$emit('update:selected-dealers', newSelected);
  }
}
